import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useSvgStore } from '../../store/svgStore';
import { SvgNodeElement, SvgGroupElement } from '../../types/svgTypes';

/** Minimap 固定像素尺寸 */
const MINIMAP_WIDTH = 200;
const MINIMAP_HEIGHT = 150;
/** 全局包围盒外扩留白（SVG 坐标） */
const WORLD_PADDING = 100;

/**
 * 画布全局小地图
 * - 缩略渲染全部元素（复用 svgContent）
 * - 半透明矩形指示当前视口
 * - 点击 / 拖拽移动视口
 */
const CanvasMinimap: React.FC = () => {
  const canvas = useSvgStore((s) => s.canvas);
  const viewport = useSvgStore((s) => s.viewport);
  const setPan = useSvgStore((s) => s.setPan);

  const svgRef = useRef<SVGSVGElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dragging, setDragging] = useState(false);

  // ─── 计算"世界"包围盒（所有元素 + 留白） ──────────────
  const worldBox = useMemo(() => {
    if (!canvas || canvas.elements.length === 0) {
      return { x: 0, y: 0, w: canvas?.width ?? 1200, h: canvas?.height ?? 800 };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    const groupChildIds = new Set<string>();
    canvas.elements.forEach((el) => {
      if (el.type === 'group') {
        (el as SvgGroupElement).children.forEach((id) => groupChildIds.add(id));
      }
    });

    for (const el of canvas.elements) {
      if (groupChildIds.has(el.id)) continue;
      const x = el.type === 'svg' ? (el as SvgNodeElement).x : (el as SvgGroupElement).x;
      const y = el.type === 'svg' ? (el as SvgNodeElement).y : (el as SvgGroupElement).y;
      const scale =
        el.type === 'svg'
          ? ((el as SvgNodeElement).scale ?? 1)
          : ((el as SvgGroupElement).scale ?? 1);

      // 简易估算: 假设每个元素 200×200，实际在 SVG 中会自动裁剪
      const estW = 200 * scale;
      const estH = 200 * scale;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + estW);
      maxY = Math.max(maxY, y + estH);
    }

    if (!isFinite(minX)) {
      return { x: 0, y: 0, w: canvas.width, h: canvas.height };
    }

    // 同时包含画布原始区域
    minX = Math.min(minX, 0);
    minY = Math.min(minY, 0);
    maxX = Math.max(maxX, canvas.width);
    maxY = Math.max(maxY, canvas.height);

    return {
      x: minX - WORLD_PADDING,
      y: minY - WORLD_PADDING,
      w: maxX - minX + WORLD_PADDING * 2,
      h: maxY - minY + WORLD_PADDING * 2,
    };
  }, [canvas]);

  // ─── 当前视口在 SVG 坐标系中的矩形 ──────────────
  const viewRect = useMemo(() => {
    if (!canvas) return { x: 0, y: 0, w: 0, h: 0 };
    return {
      x: viewport.panX,
      y: viewport.panY,
      w: canvas.width / viewport.zoom,
      h: canvas.height / viewport.zoom,
    };
  }, [canvas, viewport]);

  // ─── 鼠标坐标 → SVG 世界坐标转换 ──────────────
  const screenToWorld = useCallback(
    (clientX: number, clientY: number): { wx: number; wy: number } => {
      const svg = svgRef.current;
      if (!svg) return { wx: 0, wy: 0 };
      const rect = svg.getBoundingClientRect();
      const rx = (clientX - rect.left) / rect.width; // 0~1
      const ry = (clientY - rect.top) / rect.height;
      return {
        wx: worldBox.x + rx * worldBox.w,
        wy: worldBox.y + ry * worldBox.h,
      };
    },
    [worldBox],
  );

  /** 将点击位置设为视口中心 */
  const navigateTo = useCallback(
    (clientX: number, clientY: number) => {
      const { wx, wy } = screenToWorld(clientX, clientY);
      // 让点击位置成为视口中心
      setPan(wx - viewRect.w / 2, wy - viewRect.h / 2);
    },
    [screenToWorld, setPan, viewRect.w, viewRect.h],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(true);
      navigateTo(e.clientX, e.clientY);
    },
    [navigateTo],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      navigateTo(e.clientX, e.clientY);
    },
    [dragging, navigateTo],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setDragging(false);
  }, []);

  // ─── 渲染单个元素的缩略 ──────────────
  const renderElement = (el: SvgNodeElement | SvgGroupElement) => {
    if (el.type === 'svg') {
      const node = el as SvgNodeElement;
      const x = node.x ?? 0;
      const y = node.y ?? 0;
      const scale = node.scale ?? 1;
      const rotation = node.rotation ?? 0;
      return (
        <g
          key={node.id}
          transform={`translate(${x}, ${y}) scale(${scale}) rotate(${rotation})`}
          opacity={node.opacity ?? 1}
        >
          <g dangerouslySetInnerHTML={{ __html: node.svgContent }} />
        </g>
      );
    }

    if (el.type === 'group') {
      const group = el as SvgGroupElement;
      const x = group.x ?? 0;
      const y = group.y ?? 0;
      const scale = group.scale ?? 1;
      const rotation = group.rotation ?? 0;
      const childElements =
        canvas?.elements.filter((c) => group.children.includes(c.id)) ?? [];

      return (
        <g
          key={group.id}
          transform={`translate(${x}, ${y}) scale(${scale}) rotate(${rotation})`}
          opacity={group.opacity ?? 1}
        >
          {childElements.map((child) =>
            renderElement(child as SvgNodeElement | SvgGroupElement),
          )}
        </g>
      );
    }

    return null;
  };

  if (!canvas) return null;

  // 收集 group 子元素 id，避免重复渲染
  const groupChildIds = new Set<string>();
  canvas.elements.forEach((el) => {
    if (el.type === 'group') {
      (el as SvgGroupElement).children.forEach((id) => groupChildIds.add(id));
    }
  });

  const minimapViewBox = `${worldBox.x} ${worldBox.y} ${worldBox.w} ${worldBox.h}`;

  return (
    <div className="ai-svg-minimap-container">
      {/* 折叠按钮 */}
      <button
        className="ai-svg-minimap-toggle"
        onClick={() => setCollapsed((prev) => !prev)}
        title={collapsed ? '展开小地图' : '收起小地图'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {collapsed ? (
            // 展开图标（Maximize2）：四角向外箭头，表示"点击展开小地图"
            <>
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </>
          ) : (
            // 收起图标（Minimize2）：四角向内箭头，表示"点击收起小地图"
            <>
              <polyline points="4 14 10 14 10 20" />
              <polyline points="20 10 14 10 14 4" />
              <line x1="10" y1="14" x2="3" y2="21" />
              <line x1="21" y1="3" x2="14" y2="10" />
            </>
          )}
        </svg>
      </button>

      {!collapsed && (
        <svg
          ref={svgRef}
          className="ai-svg-minimap-svg"
          width={MINIMAP_WIDTH}
          height={MINIMAP_HEIGHT}
          viewBox={minimapViewBox}
          preserveAspectRatio="xMidYMid meet"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* 背景 */}
          <rect
            x={worldBox.x}
            y={worldBox.y}
            width={worldBox.w}
            height={worldBox.h}
            fill="#f8fafc"
          />

          {/* 画布边界 */}
          <rect
            x={0}
            y={0}
            width={canvas.width}
            height={canvas.height}
            fill="none"
            stroke="#cbd5e1"
            strokeWidth={Math.max(2, worldBox.w / 200)}
            strokeDasharray={`${worldBox.w / 80} ${worldBox.w / 80}`}
          />

          {/* 元素缩略图 - 禁用所有事件 */}
          <g pointerEvents="none">
            {canvas.elements.map((el) => {
              if (groupChildIds.has(el.id)) return null;
              return renderElement(el as SvgNodeElement | SvgGroupElement);
            })}
          </g>

          {/* 视口指示矩形 */}
          <rect
            x={viewRect.x}
            y={viewRect.y}
            width={viewRect.w}
            height={viewRect.h}
            fill="rgba(59, 130, 246, 0.12)"
            stroke="#3b82f6"
            strokeWidth={Math.max(2, worldBox.w / 300)}
            rx={Math.max(1, worldBox.w / 500)}
          />
        </svg>
      )}
    </div>
  );
};

export default CanvasMinimap;
